import type { Editor } from "@tiptap/core";
import type { ReactNode } from "react";

export interface BlockToolbarEntry {
	getPos: () => number;
	actions: ReactNode;
	groupActions?: ReactNode;
}

class BlockToolbarRegistry {
	private _entries = new Map<symbol, BlockToolbarEntry>();
	private _listeners = new Set<() => void>();
	private _version = 0;
	getSnapshot = () => this._version;
	subscribe = (listener: () => void) => {
		this._listeners.add(listener);
		return () => {
			this._listeners.delete(listener);
		};
	};
	private _notify() {
		this._version++;
		this._listeners.forEach((listener) => listener());
	}
	set(id: symbol, entry: BlockToolbarEntry) {
		this._entries.set(id, entry);
		this._notify();
	}
	delete(id: symbol) {
		this._entries.delete(id);
		this._notify();
	}
	at(pos: number) {
		return Array.from(this._entries.values()).find((entry) => positionOf(entry) === pos);
	}
}

// A node view removed by a transaction stays registered until React runs its cleanup, and its getPos throws until then.
const positionOf = (entry: BlockToolbarEntry) => {
	try {
		return entry.getPos();
	} catch {
		return null;
	}
};

const registries = new WeakMap<Editor, BlockToolbarRegistry>();
export const getBlockToolbarRegistry = (editor: Editor) => {
	let registry = registries.get(editor);
	if (!registry) {
		registry = new BlockToolbarRegistry();
		registries.set(editor, registry);
	}
	return registry;
};
